import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { NotificationActions } from './NotificationActions';
import { supabase } from '../../lib/supabase';
import type { Notification } from '../../types/notifications';

interface NotificationsListProps {
  notifications: Notification[];
  onNotificationRead: (id: string, remove?: boolean) => void;
  onClose?: () => void;
  userAvatars?: Record<string, string>;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export function NotificationsList({ 
  notifications, 
  onNotificationRead, 
  onClose, 
  userAvatars = {},
  hasMore = false,
  loadingMore = false,
  onLoadMore
}: NotificationsListProps) {
  const navigate = useNavigate();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const resolveDirectMessageRoute = async (
    conversationId: string,
    otherUserIdFromNotification?: string
  ): Promise<string> => {
    if (otherUserIdFromNotification) {
      return `/chat/${otherUserIdFromNotification}`;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return `/chat?conversation=${conversationId}`;
    }

    const { data: conversation } = await supabase
      .from('conversations')
      .select('participant_ids')
      .eq('id', conversationId)
      .maybeSingle();

    const participantIds = conversation?.participant_ids as string[] | undefined;
    const otherUserId = participantIds?.find((id) => id !== user.id);

    return otherUserId ? `/chat/${otherUserId}` : `/chat?conversation=${conversationId}`;
  };

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    if (isNearBottom && hasMore && !loadingMore && onLoadMore) {
      onLoadMore();
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read
      if (!notification.read) {
        await handleRead(notification.id);
      }
      
      // Handle navigation based on notification type
      switch (notification.type) {
        case 'new_message':
        case 'new_group_message': {
          const conversationId = notification.data.conversation_id;
          const conversationType = notification.data.conversation_type;
          if (conversationId) {
            if (conversationType === 'group') {
              navigate(`/chat?conversation=${conversationId}`);
            } else {
              const route = await resolveDirectMessageRoute(
                conversationId,
                notification.data.other_user_id
              );
              navigate(route);
            }
          }
          break;
        }
        case 'practitioner_approved':
          if (notification.data.listing_slug) {
            navigate(`/practitioners/${notification.data.listing_slug}`);
          }
          break;
        case 'event_approved':
          if (notification.data.listing_slug) {
            navigate(`/events/${notification.data.listing_slug}`);
          }
          break;
        case 'venue_approved':
          if (notification.data.listing_slug) {
            navigate(`/venues/${notification.data.listing_slug}`);
          }
          break;
        case 'job_approved':
          if (notification.data.listing_slug) {
            navigate(`/jobs/${notification.data.listing_slug}`);
          }
          break;
        case 'course_approved':
          if (notification.data.listing_slug) {
            navigate(`/courses/${notification.data.listing_slug}`);
          } else if (notification.data.listing_id) {
            // Fallback to ID if slug not available
            navigate(`/courses/${notification.data.listing_id}`);
          }
          break;
        case 'recipe_approved':
          if (notification.data.listing_id) {
            // Try to get the recipe slug, fallback to ID
            try {
              const { data: recipe } = await supabase
                .from('recipes')
                .select('slug')
                .eq('id', notification.data.listing_id)
                .single();
              
              if (recipe?.slug) {
                navigate(`/recipes/${recipe.slug}`);
              } else {
                navigate(`/recipes/${notification.data.listing_id}`);
              }
            } catch (error) {
              // Fallback to ID if slug lookup fails
              navigate(`/recipes/${notification.data.listing_id}`);
            }
          }
          break;
        case 'join_request':
        case 'join_request_approved':
        case 'join_request_rejected':
          navigate(`/communities/${notification.data.community_id}`);
          break;
        case 'community_created': {
          const communityId = notification.data?.community_id;
          const communitySlug = notification.data?.community_slug;
          if (communitySlug) {
            navigate(`/communities/${communitySlug}`);
          } else if (communityId) {
            navigate(`/communities/${communityId}`);
          }
          break;
        }
        case 'new_listing':
          // Navigate to admin dashboard for new listing notifications
          navigate('/admin');
          break;
        case 'new_booking': {
          const bookingId = notification.data?.booking_id;
          const practitionerId = notification.data?.practitioner_id || notification.data?.practitionerId;
          if (practitionerId) {
            navigate(`/bookings/${practitionerId}`);
          } else if (bookingId) {
            navigate(`/bookings`);
          }
          break;
        }
        case 'booking_status_update': {
          const practitionerId = notification.data?.practitioner_id || notification.data?.practitionerId;
          if (practitionerId) {
            navigate(`/bookings/${practitionerId}`);
          } else {
            navigate('/bookings');
          }
          break;
        }
        case 'venue_rental_new_request':
        case 'venue_rental_response':
        case 'venue_rental_cancelled': {
          let profileUser = notification.data?.profile_username as string | undefined;
          if (!profileUser) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const { data: prof } = await supabase
                .from('users')
                .select('username')
                .eq('id', user.id)
                .maybeSingle();
              profileUser = prof?.username ?? undefined;
            }
          }
          if (profileUser) {
            navigate(`/profile/${profileUser}/bookings-hub`);
          }
          break;
        }
        case 'mention':
        case 'reply':
          if (notification.data.post_type === 'timeline') {
            navigate(`/posts/${notification.data.post_id}`);
          } else if (notification.data.post_type === 'community') {
            navigate(`/communities/posts/${notification.data.post_id}`);
          }
          break;
        case 'new_follower':
          // Navigate to the follower's profile
          if (notification.data.sender_username) {
            navigate(`/profile/${notification.data.sender_username}`);
          }
          break;
        case 'follower_new_post':
          if (notification.data.post_type === 'timeline' && notification.data.sender_username) {
            navigate(`/profile/${notification.data.sender_username}`);
          } else if (notification.data.post_type === 'community' && notification.data.community_id) {
            navigate(`/communities/${notification.data.community_id}`);
          } else if (notification.data.post_type === 'conversation' && notification.data.slug) {
            navigate(`/conversations/post/${notification.data.slug}`);
          } else if (notification.data.sender_username) {
            navigate(`/profile/${notification.data.sender_username}`);
          }
          break;
        case 'follower_new_comment':
          if (notification.data.post_type === 'timeline' && notification.data.post_id) {
            navigate(`/posts/${notification.data.post_id}`);
          } else if (notification.data.post_type === 'community' && notification.data.community_id) {
            navigate(`/communities/${notification.data.community_id}`);
          } else if (notification.data.sender_username) {
            navigate(`/profile/${notification.data.sender_username}`);
          }
          break;
        case 'follower_new_listing':
          if (notification.data.community_id) {
            navigate(`/communities/${notification.data.community_id}`);
          } else if (notification.data.sender_username) {
            navigate(`/profile/${notification.data.sender_username}`);
          }
          break;
        case 'community_event':
          if (notification.data.community_id) {
            navigate(`/communities/${notification.data.community_id}`);
          }
          break;
        case 'new_community_post':
          if (notification.data.community_id) {
            navigate(`/communities/${notification.data.community_id}`);
          } else if (notification.data.post_id) {
            navigate(`/communities/posts/${notification.data.post_id}`);
          }
          break;
        default:
          // Handle other notification types
          break;
      }

      onClose?.();
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const handleRead = async (id: string, remove = false) => {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);

    if (!error) {
      onNotificationRead(id, remove);
    }
  };

  return (
    <div 
      ref={scrollRef}
      className="max-h-96 overflow-y-auto"
      onScroll={handleScroll}
    >
      {notifications.length === 0 ? (
        <div className="p-4 text-center text-gray-500">
          No notifications
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transform transition-all duration-200 ${
                  !notification.read ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start">
                  {notification.type === 'field_invitation' ? (
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🌱</span>
                    </div>
                  ) : (
                    <Avatar
                      url={notification.data?.sender_avatar_url || userAvatars[notification.data?.sender_id]}
                      size="sm"
                      userId={notification.data?.sender_id}
                      editable={false}
                    />
                  )}
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {notification.title}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {notification.message}
                    </p>
                    <NotificationActions 
                      notification={notification}
                      onAction={(id) => handleRead(id, true)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {loadingMore && (
            <div className="p-4 flex justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent-text"></div>
            </div>
          )}
          
          {!hasMore && notifications.length > 10 && (
            <div className="p-4 text-center text-gray-500 text-sm">
              No more notifications
            </div>
          )}
        </>
      )}
    </div>
  );
}